import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
    BookOpen, Layers, FileText,
    ChevronDown, ChevronUp, CheckCircle, AlertCircle,
    Zap, ScrollText, Brain,
    ChevronRight, Star, Target, Stethoscope, ArrowRight,
    Heart, HeartPulse, Activity, Pill, Syringe, Microscope,
    Thermometer, Dna, FlaskConical, TestTube, Baby, Bone, Scan,
    ClipboardList, Award, Lightbulb, ListChecks, GraduationCap, Eye,
    Ear, Wind, HeartCrack, Waves, Droplets, Ambulance, Siren, Cross, BriefcaseMedical,
    Sparkles, LogIn, CreditCard, Gift
} from 'lucide-react';
import { DescBlock } from '../../components/admin/products/tabs/CreateProducts';
import ApostilaDemoBlock, { ApostilaDemoData, defaultApostilaDemoData } from '../components/product-blocks/ApostilaDemoBlock';
import FlashcardsDemoBlock, { FlashcardsDemoData, defaultFlashcardsDemoData } from '../components/product-blocks/FlashcardsDemoBlock';
import MetodoDemoBlock, { MetodoDemoData, defaultMetodoDemoData } from '../components/product-blocks/MetodoDemoBlock';
import EstudoAtivoDemoBlock, { EstudoAtivoDemoData, defaultEstudoAtivoDemoData } from '../components/product-blocks/EstudoAtivoDemoBlock';
import TutoriaDemoBlock, { TutoriaDemoData, defaultTutoriaDemoData } from '../components/product-blocks/TutoriaDemoBlock';
import VideoDemoBlock, { VideoDemoData, defaultVideoDemoData } from '../components/product-blocks/VideoDemoBlock';
import { getRyanMedicalIcon } from '../constants/ryanMedicalIcons';
import { getRyanHeroFontFamily } from '../constants/ryanHeroFonts';
import RyanProductHeroPattern from '../components/RyanProductHeroPattern';
import RyanTestModeBadge from '../components/RyanTestModeBadge';
import { makeRyanTracker } from '../utils/ryanAnalytics';
import './RyanProductPage.css';

// ── Tipos ──────────────────────────────────────────────────────────────

interface SubdeckItem {
    _id: string;
    name: string;
    description: string;
    type?: 'apostila' | 'flashcards';
    cardCount?: number;
    // Nº de páginas (só apostilas — Resumo.pages)
    pageCount?: number;
    customIcon?: string | null;
    previewUrl?: string | null;
    // Marcado como "Deixar gratuito" (RyanFieldPage) — qualquer conta logada
    // acessa sem compra/liberação. Mostra o mesmo badge de presente da
    // RyanFieldPage aqui na página pública, que leva ao cadastro.
    isFree?: boolean;
}

interface ContentContainer {
    id: string;
    name: string;
    color?: string;
    icon?: string | null;
    // Texto livre do modo "Personalizado" (customSections), exibido embaixo
    // do nome da seção — ver server/models/RyanProduct.js.
    description?: string;
    items?: SubdeckItem[];
    containers?: ContentContainer[];
}

interface SpaceData {
    _id: string;
    name: string;
    description: string;
    apostilas: SubdeckItem[];
    subdecks: SubdeckItem[];
    totalCards: number;
    organization?: { containers: ContentContainer[] };
}

interface Totals {
    spaces: number;
    apostilas: number;
    subdecks: number;
    flashcards: number;
}

interface ContentBlockItem {
    id: string;
    type: 'apostila_demo' | 'flashcards_demo' | 'metodo_demo' | 'estudo_ativo_demo' | 'tutoria_demo' | 'video_demo';
    enabled: boolean;
    order: number;
    backgroundVariant?: 'white' | 'green' | 'light' | 'dark';
    data: ApostilaDemoData | FlashcardsDemoData | MetodoDemoData | EstudoAtivoDemoData | TutoriaDemoData | VideoDemoData;
}

interface OsceScenario {
    badge: string;
    examinerLabel: string;
    question: string;
    hint: string;
    answerLabel: string;
    answerText: string;
}

interface RyanProductData {
    _id: string;
    title: string;
    priceCents: number;
    validadeTexto: string;
    periodo: number | null;
    publicPage: {
        pixKey: string;
        whatsappNumber: string;
        pixHolderName: string;
        pixQrCodeDataUrl: string | null;
        showPriceHero: boolean;
        showPriceClose: boolean;
        showStats: boolean;
        heroTitleFont?: string;
        gridCols: number;
        contentOrganization?: 'auto' | 'tabs' | 'categories' | 'flat' | 'custom';
        contentDropdown?: boolean;
        heroDescription: string;
        statsMode: 'auto' | 'custom';
        statsCustomContent: string;
        differentialEnabled: boolean;
        differentialTitle: string;
        differentialContent: string;
        differentialRightMode: 'none' | 'animation_osce' | 'stat_cards';
        differentialStatCards: Array<{ icon: string; value: string; label: string }>;
        osceScenario?: OsceScenario;
        contentBlocks?: ContentBlockItem[];
        // Contagem de flashcards/decks no bloco "Conteúdo incluído" — geral
        // (chips agregados) e individual (badge por item). Default true no
        // schema, então undefined aqui (produto salvo antes deste campo
        // existir) deve ser tratado como mostrar (ver uso abaixo com ?? true).
        showContentGroupCounts?: boolean;
        showContentItemCounts?: boolean;
    };
    content: {
        spaces: SpaceData[];
        totals: Totals;
        // Só presente quando publicPage.contentOrganization === 'custom' —
        // agrupamento GLOBAL (não por space) definido à mão pelo admin, ver
        // resolveProductSpaceScopes/customSections em ryanRoutes.js.
        customOrganization?: { containers: ContentContainer[] } | null;
    } | null;
    // As recomendações de upsell (RyanUpsellOffer) também vêm nesse payload,
    // mas só são consumidas na RyanCheckoutPage — a página pública do produto
    // não precisa delas, então não são declaradas aqui.
}

// ── Mapa de ícones ──────────────────────────────────────────────────────
const ICON_MAP: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
    CheckCircle, ChevronRight, BookOpen, Zap, Star,
    Brain, FileText, Target, Stethoscope, ArrowRight,
    Layers, ScrollText,
    Heart, HeartPulse, Activity, Pill, Syringe, Microscope,
    Thermometer, Dna, FlaskConical, TestTube, Baby, Bone, Scan,
    ClipboardList, Award, Lightbulb, ListChecks, GraduationCap, Eye, Ear,
    Wind, HeartCrack, Waves, Droplets, Ambulance, Siren, Cross, BriefcaseMedical,
    Sparkles
};

function renderDescBlocks(raw: string): React.ReactNode {
    if (!raw) return null;
    let blocks: DescBlock[] = [];
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) blocks = parsed;
        else blocks = [{ type: 'p', text: raw }];
    } catch {
        blocks = [{ type: 'p', text: raw }];
    }
    return (
        <div className="rpp-desc-blocks">
            {blocks.map((b, i) => {
                if (b.type === 'p') return <p key={i} className="rpp-desc-p">{b.text}</p>;
                const IconComp = b.icon ? (ICON_MAP[b.icon] ?? ChevronRight) : ChevronRight;
                return (
                    <div key={i} className="rpp-desc-li">
                        <IconComp size={13} className="rpp-desc-li-icon" />
                        <span>{b.text}</span>
                    </div>
                );
            })}
        </div>
    );
}

function formatPrice(cents: number): string {
    return `R$ ${(cents / 100).toFixed(2).replace('.', ',')}`;
}

// Tingimento sutil do fundo/borda dos blocos de aba/categoria a partir da cor
// própria (mesmo padrão já usado em TutoriaDemoBlock.tsx) — hex malformado
// (dado externo, vindo do admin/backend) nunca deveria lançar exceção aqui,
// só cair no verde neutro da marca.
function hexToRgba(hex: string, alpha: number): string {
    const clean = (hex || '').replace('#', '').trim();
    const full = clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean;
    const r = parseInt(full.substring(0, 2), 16);
    const g = parseInt(full.substring(2, 4), 16);
    const b = parseInt(full.substring(4, 6), 16);
    if ([r, g, b].some(Number.isNaN)) return `rgba(47, 113, 48, ${alpha})`;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function buildWhatsappUrl(number: string, productTitle: string, message?: string): string {
    const cleaned = number.replace(/\D/g, '');
    const full = cleaned.startsWith('55') ? cleaned : `55${cleaned}`;
    const msg = encodeURIComponent(
        message ?? `Olá! Tenho uma dúvida sobre "${productTitle}".`
    );
    return `https://wa.me/${full}?text=${msg}`;
}

// Reflete o fluxo real de hoje: pagamento automático via Mercado Pago (Pix ou
// cartão) direto no checkout (/ryan/checkout/:slug), conta criada no mesmo
// passo se ainda não existir, acesso liberado automaticamente após
// confirmação — sem comprovante, sem espera por confirmação manual, sem
// WhatsApp no meio da liberação (só como canal de dúvidas, à parte).
const CLOSE_FAQ: { q: string; a: string }[] = [
    { q: 'É seguro pagar pelo site?', a: 'Sim. Todo o pagamento é processado diretamente pelo Mercado Pago, utilizando os mesmos padrões de segurança adotados por milhares de empresas. Os dados do seu cartão nunca passam pelos nossos servidores.' },
    { q: 'Quando recebo meu acesso?', a: 'Assim que o Mercado Pago confirma o pagamento, o acesso é liberado automaticamente na plataforma. No Pix isso costuma acontecer em poucos segundos; no cartão, depende apenas da aprovação da operadora.' },
    { q: 'Preciso criar uma conta antes de comprar?', a: 'Não. Basta informar seu e-mail durante o checkout. Se você já possui uma conta, ela será identificada automaticamente. Caso contrário, uma nova conta será criada no mesmo processo da compra.' },
    { q: 'Posso falar com alguém antes de comprar?', a: 'Sim. Se restar qualquer dúvida sobre o material ou sobre a compra, é só entrar em contato pelo WhatsApp através do link disponível no rodapé da página.' },
];

const WhatsAppIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
);

function useScrollReveal(active: boolean) {
    useEffect(() => {
        if (!active) return;
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach(e => {
                    if (e.isIntersecting) {
                        const delay = (e.target as HTMLElement).dataset.delay || '0';
                        (e.target as HTMLElement).style.setProperty('--rpp-delay', `${delay}ms`);
                        // data-attribute, não classe: `className` é recalculado pelo React a
                        // cada render (ex: toggle de abrir/fechar item) e sobrescreve o atributo
                        // `class` inteiro, apagando silenciosamente qualquer classe adicionada
                        // por fora do React (como o `.rpp-in` que era setado aqui antes) — um
                        // data-attribute nunca é tocado pela reconciliação do React porque não
                        // faz parte de nenhuma prop do JSX, então sobrevive a qualquer re-render.
                        (e.target as HTMLElement).setAttribute('data-revealed', 'true');
                        obs.unobserve(e.target);
                    }
                });
            },
            { threshold: 0.08, rootMargin: '-10px 0px -10px 0px' }
        );
        requestAnimationFrame(() => {
            document.querySelectorAll('.rpp-anim').forEach(el => obs.observe(el));
        });
        return () => obs.disconnect();
    }, [active]);
}

const DEFAULT_OSCE_SCENARIO: OsceScenario = {
    badge: 'Estação OSCE · Cardiologia',
    examinerLabel: 'O examinador pergunta:',
    question: 'Descreva o sopro esperado: momento do ciclo cardíaco, caráter, foco de máxima intensidade e irradiação.',
    hint: 'Responda em voz alta, depois confira:',
    answerLabel: 'Descrição do sopro',
    answerText: 'Sopro sistólico ejetivo, crescendo-decrescendo (em diamante), rude e granular. Foco de máxima intensidade no 2° EIC direito (foco aórtico). Irradia para a fúrcula esternal e carótidas.',
};

const OsceAnimation: React.FC<{ scenario?: OsceScenario }> = ({ scenario }) => {
    const [phase, setPhase] = useState<'blur' | 'clicking' | 'revealed'>('blur');
    const s = scenario || DEFAULT_OSCE_SCENARIO;

    useEffect(() => {
        const timers: ReturnType<typeof setTimeout>[] = [];
        const run = () => {
            setPhase('blur');
            timers.push(setTimeout(() => setPhase('clicking'), 2800));
            timers.push(setTimeout(() => setPhase('revealed'), 3200));
            timers.push(setTimeout(() => setPhase('blur'), 7000));
            timers.push(setTimeout(run, 7800));
        };
        const init = setTimeout(run, 400);
        return () => { clearTimeout(init); timers.forEach(clearTimeout); };
    }, []);

    return (
        <div className="rpp-osce-wrap">
            <div className="rpp-osce-titlebar">
                <div className="rpp-osce-dots"><span /><span /><span /></div>
                <span className="rpp-osce-title-text">Graia Medicina — Cenário Clínico</span>
            </div>
            <div className="rpp-osce-body">
                <div className="rpp-osce-badge"><Stethoscope size={11} /> {s.badge}</div>
                <p className="rpp-osce-examiner-label">{s.examinerLabel}</p>
                <p className="rpp-osce-question">{s.question}</p>
                <p className="rpp-osce-hint">{s.hint}</p>
                <div className={`rpp-osce-answer-wrap ${phase === 'revealed' ? 'rpp-revealed' : ''}`}>
                    <div className="rpp-osce-answer-label">{s.answerLabel}</div>
                    <div className="rpp-osce-answer-text">{s.answerText}</div>
                </div>
                {phase !== 'revealed' ? (
                    <button className={`rpp-osce-btn${phase === 'clicking' ? ' rpp-osce-btn-click' : ''}`} tabIndex={-1}>
                        <Eye size={13} /> Ver resposta
                    </button>
                ) : (
                    <div className="rpp-osce-check"><CheckCircle size={14} /> Confira sua resposta!</div>
                )}
            </div>
        </div>
    );
};

// Isola falhas de um bloco de demonstração (ex: dado salvo em formato antigo)
// para que elas nunca derrubem o resto da página pública.
class DemoBlockErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
    state = { hasError: false };

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error: unknown) {
        console.error('Erro ao renderizar bloco de demonstração:', error);
    }

    render() {
        if (this.state.hasError) return null;
        return this.props.children;
    }
}

// ── Rastreamento de link de embaixador (?rep=CODIGO) ──────────────────────
// Só grava aqui — quem lê (com expiração de 30 dias) é a RyanCheckoutPage,
// que é onde o código de indicação realmente é usado (solicitar-acesso/
// aceitar-recomendacoes). Mesma chave de localStorage (`ryan_ref_${slug}`)
// nos dois lugares.
function saveReferralCode(slug: string, code: string) {
    try {
        localStorage.setItem(`ryan_ref_${slug}`, JSON.stringify({ code, savedAt: Date.now() }));
    } catch { /* localStorage indisponível — segue sem rastrear */ }
}

// ══════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ══════════════════════════════════════════════════════════════════════

const RyanProductPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const [searchParams] = useSearchParams();
    const [product, setProduct] = useState<RyanProductData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expandedSpaces, setExpandedSpaces] = useState<Set<string>>(new Set());
    const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
    const [expandedContainers, setExpandedContainers] = useState<Set<string>>(new Set());
    const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
    const navigate = useNavigate();

    // ── "Jornada contínua" do fechamento (rpp-close-section): um único ponto
    // de luz que percorre os 3 passos até o botão, uma única vez por
    // carregamento. Observer DEDICADO (não o useScrollReveal genérico acima,
    // que só liga/desliga opacidade via data-revealed) porque aqui a entrada
    // em viewport dispara uma CASCATA de estágios no tempo, não uma classe
    // estática — precisa do próprio ciclo de vida/effect só dela.
    const closeSectionRef = useRef<HTMLElement>(null);
    // Ref, não state: precisa sobreviver a re-renders sem disparar um novo
    // render sozinho, e precisa ser lido de forma síncrona dentro do
    // callback do IntersectionObserver pra garantir que, mesmo que o
    // usuário suba e desça repetidamente antes do primeiro setState
    // re-renderizar o componente, o estágio 1 nunca dispare duas vezes.
    const closeJourneyPlayedRef = useRef(false);
    const [closeJourneyActive, setCloseJourneyActive] = useState(false);
    // 0 = parado/idle. 1 = brilho no passo 1. 2 = travessia 1→2. 3 = brilho
    // no passo 2. 4 = travessia 2→3. 5 = brilho no passo 3. 6 = queda até o
    // botão. 7 = pulso do botão. 8 = encerrado (estático até o próximo ciclo).
    const [closeJourneyStage, setCloseJourneyStage] = useState(0);
    // Incrementa a cada replay do ciclo (a cada ~60s depois do estágio 8) —
    // o effect abaixo depende dele, não só de closeJourneyActive: um boolean
    // que volta pro mesmo valor (true → true) dentro do mesmo lote de
    // atualização do React não dispara o effect de novo, um contador que
    // sempre muda sim.
    const [closeJourneyCycle, setCloseJourneyCycle] = useState(0);

    useScrollReveal(!!product);

    // Marcos de scroll (25/50/75/100%) em vez de porcentagem contínua — throttle
    // via rAF (não a cada pixel) pra não competir com as animações rpp-anim que
    // a página já dispara em resposta ao mesmo scroll.
    useEffect(() => {
        if (!product || !slug) return;
        const tracker = makeRyanTracker(product._id, slug, 'product');
        const firedMilestones = new Set<number>();
        const MILESTONES = [25, 50, 75, 100];
        let ticking = false;
        const handleScroll = () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => {
                ticking = false;
                const scrollable = document.documentElement.scrollHeight - window.innerHeight;
                if (scrollable <= 0) return;
                const percent = Math.min(100, Math.round((window.scrollY / scrollable) * 100));
                for (const m of MILESTONES) {
                    if (percent >= m && !firedMilestones.has(m)) {
                        firedMilestones.add(m);
                        tracker.track('scroll_depth', { percent: m });
                    }
                }
            });
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [product, slug]);

    useEffect(() => {
        if (!product || closeJourneyPlayedRef.current) return;
        const el = closeSectionRef.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach(e => {
                    if (e.isIntersecting && !closeJourneyPlayedRef.current) {
                        closeJourneyPlayedRef.current = true;
                        obs.disconnect();
                        // Delay antes do primeiro play — dá tempo de ler o
                        // passo a passo antes da luz começar a se mover, em
                        // vez da animação já estar rolando no instante em
                        // que a seção aparece na tela.
                        setTimeout(() => setCloseJourneyActive(true), 3500);
                    }
                });
            },
            { threshold: 0.3 }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [product]);

    useEffect(() => {
        if (!closeJourneyActive) return;
        if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return; // pula a animação inteira — seção fica estática, como hoje
        }
        // Durações ~50% maiores que a rodada anterior (ritmo mais pausado,
        // dentro da faixa de +40-60% pedida) — mantém a mesma sequência de
        // estágios, só mais lenta.
        const timers: ReturnType<typeof setTimeout>[] = [];
        let cursor = 220;
        timers.push(setTimeout(() => setCloseJourneyStage(1), cursor)); // brilho no passo 1
        cursor += 600;
        timers.push(setTimeout(() => setCloseJourneyStage(2), cursor)); // travessia 1 → 2
        cursor += 900;
        timers.push(setTimeout(() => setCloseJourneyStage(3), cursor)); // brilho no passo 2
        cursor += 520;
        timers.push(setTimeout(() => setCloseJourneyStage(4), cursor)); // travessia 2 → 3
        cursor += 900;
        timers.push(setTimeout(() => setCloseJourneyStage(5), cursor)); // brilho no passo 3
        cursor += 520;
        timers.push(setTimeout(() => setCloseJourneyStage(6), cursor)); // queda até o botão
        cursor += 820;
        timers.push(setTimeout(() => setCloseJourneyStage(7), cursor)); // pulso do botão
        cursor += 800;
        timers.push(setTimeout(() => setCloseJourneyStage(8), cursor)); // encerrado — estático até o replay
        // Reinicia sozinha ~60s depois de terminar, em vez de ficar estática
        // pra sempre depois da primeira vez.
        cursor += 60000;
        timers.push(setTimeout(() => {
            setCloseJourneyStage(0);
            setCloseJourneyCycle(c => c + 1);
        }, cursor));
        return () => timers.forEach(clearTimeout);
    }, [closeJourneyActive, closeJourneyCycle]);

    useEffect(() => { if (slug) fetchProduct(slug); }, [slug]);

    useEffect(() => {
        const rep = searchParams.get('rep');
        if (slug && rep) saveReferralCode(slug, rep);
    }, [slug, searchParams]);

    useEffect(() => {
        document.title = product ? `${product.title} | Ryan` : 'Ryan';
        return () => { document.title = 'Ryan'; };
    }, [product]);

    useEffect(() => {
        if (product?.content?.spaces?.length) {
            setExpandedSpaces(new Set([product.content.spaces[0]._id]));
        }
    }, [product]);

    const fetchProduct = async (s: string) => {
        try {
            const res = await fetch(`/api/ryan/produto/${s}`);
            if (!res.ok) throw new Error('not_found');
            const data = await res.json();
            setProduct(data.product);
            makeRyanTracker(data.product._id, s, 'product').track('pageview');
        } catch {
            setError('Produto não encontrado.');
        } finally {
            setLoading(false);
        }
    };

    const toggleSpace = (id: string) => {
        setExpandedSpaces(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    if (loading) {
        return (
            <div className="rpp-loading">
                <div className="rpp-spinner" />
                <span>Carregando...</span>
            </div>
        );
    }

    if (error || !product) {
        return (
            <div className="rpp-error">
                <div className="rpp-error-icon"><AlertCircle size={28} /></div>
                <h2>Produto indisponível</h2>
                <p>Este link não existe ou está temporariamente desativado.</p>
                <a href="/ryan" className="rpp-error-link">Voltar ao Ryan →</a>
            </div>
        );
    }

    const { content, publicPage } = product;
    const totals = content?.totals;
    const spaces = content?.spaces ?? [];
    const whatsappDoubtUrl = publicPage.whatsappNumber
        ? buildWhatsappUrl(publicPage.whatsappNumber, product.title, `Olá! Tenho uma dúvida sobre "${product.title}" antes de comprar.`)
        : '#';

    // Botão "Acessar prévias" no Hero: automático, sem toggle nem texto
    // configurável no admin — só aparece quando existe pelo menos um link de
    // prévia configurado em algum subdeck (senão não haveria nada pra destacar).
    const hasPreviews = spaces.some(sp => [...sp.apostilas, ...sp.subdecks].some(it => !!it.previewUrl));

    // Rola até "Conteúdo Incluído" e destaca temporariamente os badges de prévia
    // (em vez de abrir um link externo — a prévia já está ali embaixo). Abre
    // todos os campos E todas as abas/categorias antes de rolar — os badges já
    // ficam no DOM mesmo com o bloco fechado (dropdown some só via CSS), mas
    // fechado ninguém consegue VER o brilho; sem isso o clique em "Acessar
    // prévias" levava pra uma seção com tudo escondido atrás de dropdowns.
    const scrollToPreviews = () => {
        if (slug) makeRyanTracker(product._id, slug, 'product').track('click_preview', { source: 'hero' });
        setExpandedSpaces(new Set(spaces.map(sp => sp._id)));
        setExpandedContainers(new Set([
            ...spaces.flatMap(sp => (sp.organization?.containers || []).map(c => c.id)),
            ...(customContainers || []).map(c => c.id),
        ]));
        document.getElementById('rpp-content-section')?.scrollIntoView({ behavior: 'smooth' });
        const badges = document.querySelectorAll('.rpp-preview-badge');
        badges.forEach(b => b.classList.add('rpp-preview-badge--glow'));
        setTimeout(() => {
            badges.forEach(b => b.classList.remove('rpp-preview-badge--glow'));
        }, 5000);
    };

    const toggleItemOpen = (id: string) => setExpandedItems(prev => {
        const n = new Set(prev);
        n.has(id) ? n.delete(id) : n.add(id);
        return n;
    });

    const goToSignup = (source: string, itemId: string) => {
        if (slug) makeRyanTracker(product._id, slug, 'product').track('click_preview', { source, itemId, action: 'free_badge_signup' });
        navigate('/ryan/login?mode=signup');
    };

    const renderContentItem = (item: SubdeckItem, index: number = 0) => {
        const isApostila = item.type === 'apostila';
        const Icon = getRyanMedicalIcon(item.customIcon) ?? (isApostila ? FileText : BookOpen);
        const hasDesc = !!item.description;
        const isOpen = expandedItems.has(item._id);
        const gridCols = publicPage.gridCols || 3;
        const showItemCount = publicPage.showContentItemCounts ?? true;
        const freeBadge = item.isFree && (
            <button
                type="button"
                className="rpp-free-badge"
                title="Crie sua conta e aproveite conteúdo gratuito!"
                onClick={e => { e.stopPropagation(); goToSignup('item_badge', item._id); }}
            >
                <Gift size={11} /> Gratuito
            </button>
        );
        return (
            <div key={item._id}
                className={`rpp-item rpp-item--${isApostila ? 'amber' : 'blue'} ${isOpen ? 'rpp-item-open' : ''} rpp-anim rpp-anim--subtle`}
                data-delay={(index % gridCols) * 50}
            >
                <div className="rpp-item-top">
                    <div className="rpp-item-icon"><Icon size={14} /></div>
                    <p className="rpp-item-name">{item.name}</p>
                    {isApostila ? (
                        <>
                            {freeBadge}
                            {item.previewUrl && (
                                <a href={item.previewUrl} target="_blank" rel="noopener noreferrer"
                                    className="rpp-preview-badge"
                                    onClick={e => {
                                        e.stopPropagation();
                                        if (slug) makeRyanTracker(product._id, slug, 'product').track('click_preview', { source: 'item_badge', itemId: item._id });
                                    }}>
                                    Prévia
                                </a>
                            )}
                            {showItemCount && (item.pageCount ?? 0) > 0 && (
                                <span className="rpp-card-count rpp-card-count--pages">{item.pageCount} pág.</span>
                            )}
                            {hasDesc && (
                                <button className="rpp-item-toggle" onClick={() => toggleItemOpen(item._id)}
                                    aria-label={isOpen ? 'Fechar' : 'Abrir'}>
                                    {isOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                                </button>
                            )}
                        </>
                    ) : (
                        <div className="rpp-item-top-right">
                            {freeBadge}
                            {item.previewUrl && (
                                <a href={item.previewUrl} target="_blank" rel="noopener noreferrer"
                                    className="rpp-preview-badge"
                                    onClick={e => {
                                        e.stopPropagation();
                                        if (slug) makeRyanTracker(product._id, slug, 'product').track('click_preview', { source: 'item_badge', itemId: item._id });
                                    }}>
                                    Prévia
                                </a>
                            )}
                            {showItemCount && (item.cardCount ?? 0) > 0 && (
                                <span className="rpp-card-count">{item.cardCount}</span>
                            )}
                            {hasDesc && (
                                <button className="rpp-item-toggle" onClick={() => toggleItemOpen(item._id)}
                                    aria-label={isOpen ? 'Fechar' : 'Abrir'}>
                                    {isOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                                </button>
                            )}
                        </div>
                    )}
                </div>
                {hasDesc && isOpen && (
                    <div className="rpp-item-desc">{renderDescBlocks(item.description)}</div>
                )}
            </div>
        );
    };

    const toggleContainerOpen = (id: string) => setExpandedContainers(prev => {
        const n = new Set(prev);
        n.has(id) ? n.delete(id) : n.add(id);
        return n;
    });

    const renderContentContainer = (container: ContentContainer): React.ReactNode => {
        if (container.containers && container.containers.length > 0) {
            return (
                <div key={container.id} className="rpp-org-tab">
                    <div className="rpp-org-tab-label">{container.name}</div>
                    <div className="rpp-org-tab-body">
                        {container.containers.map(renderContentContainer)}
                    </div>
                </div>
            );
        }
        const items = container.items || [];
        if (items.length === 0) return null;
        const useDropdown = !!publicPage.contentDropdown;
        const isOpen = !useDropdown || expandedContainers.has(container.id);
        const showGroupCount = publicPage.showContentGroupCounts ?? true;
        const apostilaCount = items.filter(it => it.type === 'apostila').length;
        const deckCount = items.length - apostilaCount;
        const cardCount = items.reduce((sum, it) => sum + (it.cardCount ?? 0), 0);
        // Sempre o verde da marca (--rpp-mid): a página pública tem identidade
        // própria — a cor de categoria/grupo escolhida na RyanFieldPage é da
        // organização interna do campo e não vale aqui (ex.: o dourado-escuro
        // padrão das apostilas virava um selo marrom destoando da página).
        const accentColor = '#2F7130';
        // Sem ícone próprio (ex.: categorias espelhadas da RyanFieldPage), usa o
        // mesmo par do painel admin: cérebro pra decks, pergaminho pra apostilas
        const FallbackBadgeIcon = apostilaCount === 0 ? Brain : deckCount === 0 ? ScrollText : Layers;
        const BadgeIcon = (container.icon ? getRyanMedicalIcon(container.icon) : null) || FallbackBadgeIcon;
        return (
            <div
                key={container.id}
                className={`rpp-org-block${useDropdown ? ' rpp-org-block--dropdown' : ''}${isOpen ? ' expanded' : ''}`}
                style={{
                    '--rpp-org-tint': hexToRgba(accentColor, 0.055),
                    '--rpp-org-border': hexToRgba(accentColor, 0.32),
                    '--rpp-org-shadow': hexToRgba(accentColor, 0.26),
                } as React.CSSProperties}
            >
                <div
                    className="rpp-org-block-header"
                    {...(useDropdown ? {
                        onClick: () => toggleContainerOpen(container.id),
                        role: 'button' as const,
                        tabIndex: 0,
                    } : {})}
                >
                    <span className="rpp-org-block-badge" style={{ background: accentColor }}>
                        <BadgeIcon size={17} />
                    </span>
                    <div className="rpp-org-block-heading">
                        <p className="rpp-org-block-name">{container.name}</p>
                        {container.description && (
                            <p className="rpp-org-block-desc">{container.description}</p>
                        )}
                        {showGroupCount && (
                            <div className="rpp-org-block-chips">
                                {apostilaCount > 0 && (
                                    <span className="rpp-chip rpp-chip--amber">
                                        <ScrollText size={10} />{apostilaCount} apostil{apostilaCount > 1 ? 'as' : 'a'}
                                    </span>
                                )}
                                {deckCount > 0 && (
                                    <span className="rpp-chip rpp-chip--blue">
                                        <Brain size={10} />{deckCount} deck{deckCount > 1 ? 's' : ''}
                                    </span>
                                )}
                                {cardCount > 0 && (
                                    <span className="rpp-chip rpp-chip--purple">
                                        <Zap size={10} />{cardCount.toLocaleString('pt-BR')} cards
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                    {useDropdown && <ChevronDown size={16} className="rpp-org-block-chevron" />}
                </div>
                <div className="rpp-org-block-body">
                    <div className="rpp-org-block-body-inner">
                        <div className="rpp-items-grid" style={{ '--rpp-grid-cols': publicPage.gridCols || 3 } as React.CSSProperties}>
                            {items.map(renderContentItem)}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderSpaceContent = (space: SpaceData): React.ReactNode => {
        if (space.organization && space.organization.containers.length > 0) {
            return <>{space.organization.containers.map(renderContentContainer)}</>;
        }
        return (
            <>
                {space.apostilas.length > 0 && (
                    <div className="rpp-subdeck-group">
                        <div className="rpp-group-label rpp-group-label--amber">
                            <ScrollText size={13} /> Apostilas
                        </div>
                        <div className="rpp-items-grid" style={{ '--rpp-grid-cols': publicPage.gridCols || 3 } as React.CSSProperties}>
                            {space.apostilas.map(renderContentItem)}
                        </div>
                    </div>
                )}
                {space.subdecks.length > 0 && (
                    <div className="rpp-subdeck-group">
                        <div className="rpp-group-label rpp-group-label--blue">
                            <Brain size={13} /> Decks de Flashcards
                        </div>
                        <div className="rpp-items-grid" style={{ '--rpp-grid-cols': publicPage.gridCols || 3 } as React.CSSProperties}>
                            {space.subdecks.map(renderContentItem)}
                        </div>
                    </div>
                )}
            </>
        );
    };

    // Produto single + organização não-flat (abas ou categorias): o Hero (tema do
    // Space) já comunica visualmente qual é o campo, então o card com o nome do
    // Space vira redundante — some daqui. 'auto' é valor legado, tratado como
    // não-flat (mesma lógica do backend em ryanRoutes.js).
    const isSingleAuto = spaces.length === 1 && (publicPage.contentOrganization ?? 'tabs') !== 'flat';

    // "Personalizado" — agrupamento GLOBAL definido à mão pelo admin (não por
    // space/aba real), ver customSections em RyanAdminProducts.tsx. Quando
    // ativo, ignora inteiramente a divisão por space (nem cards de campo, nem
    // isSingleAuto) — é UM bloco de seções só, cruzando qualquer campo/aba
    // incluído no produto.
    const customContainers = publicPage.contentOrganization === 'custom'
        ? (content?.customOrganization?.containers || [])
        : null;

    // Derivados do estágio único da jornada de luz (ver closeJourneyStage acima)
    // — cada um só é `true` na janela exata do seu instante na história.
    const closeGlowStep1 = closeJourneyStage === 1;
    const closeTravel12 = closeJourneyStage === 2;
    const closeGlowStep2 = closeJourneyStage === 3;
    const closeTravel23 = closeJourneyStage === 4;
    const closeGlowStep3 = closeJourneyStage === 5;
    const closeTravelDrop = closeJourneyStage === 6;
    const closeButtonPulse = closeJourneyStage === 7;

    return (
        <div className="rpp-root">
            <RyanTestModeBadge />

            {/* ── HERO ── */}
            <section className="rpp-hero">
                <div className="rpp-hero-pattern">
                    <RyanProductHeroPattern />
                </div>
                <div className="rpp-hero-inner">
                    <div className="rpp-hero-brand">
                        <img src="/Ryan/graia-logo-branca.png" alt="GRAIA" className="rpp-hero-logo" />
                    </div>

                    <h1 className="rpp-hero-title" style={{ fontFamily: getRyanHeroFontFamily(publicPage.heroTitleFont) }}>
                        {product.title}
                    </h1>
                    <div className="rpp-hero-divider">
                        <span className="rpp-hero-divider-line" />
                        <span className="rpp-hero-divider-dot" />
                        <span className="rpp-hero-divider-line" />
                    </div>

                    {publicPage.heroDescription && (
                        <p className="rpp-hero-description">{publicPage.heroDescription}</p>
                    )}

                    {publicPage.showPriceHero && product.priceCents > 0 && (
                        <div className="rpp-hero-price-badge">
                            <span className="rpp-hero-price-value">{formatPrice(product.priceCents)}</span>
                        </div>
                    )}

                    <div className="rpp-hero-buttons-row">
                        <button className="rpp-hero-cta" onClick={() => {
                            if (slug) makeRyanTracker(product._id, slug, 'product').track('click_buy', { source: 'hero' });
                            navigate(`/ryan/checkout/${slug}`);
                        }}>
                            <CheckCircle size={17} /> Garantir meu acesso
                        </button>
                        {hasPreviews && (
                            <button type="button" className="rpp-hero-preview-btn" onClick={scrollToPreviews}>
                                <Zap size={15} />
                                Acessar prévias
                            </button>
                        )}
                    </div>
                </div>
            </section>

            {/* ── STATS ── */}
            {publicPage.showStats && (
                <section className="rpp-stats-section rpp-anim">
                    <div className="rpp-stats-inner">
                        <p className="rpp-stats-eyebrow">O que você recebe</p>

                        {publicPage.statsMode === 'custom' && publicPage.statsCustomContent ? (
                            <div className="rpp-stats-custom">
                                {renderDescBlocks(publicPage.statsCustomContent)}
                            </div>
                        ) : (
                            totals && (totals.spaces > 0 || totals.flashcards > 0) && (
                                <div className="rpp-stats-grid">
                                    {totals.spaces > 1 && (
                                        <div className="rpp-stat-card rpp-anim" data-delay="0">
                                            <div className="rpp-stat-icon rpp-stat-icon--green"><BookOpen size={18} /></div>
                                            <span className="rpp-stat-number">{totals.spaces}</span>
                                            <span className="rpp-stat-label">Campos</span>
                                        </div>
                                    )}
                                    {totals.apostilas > 0 && (
                                        <div className="rpp-stat-card rpp-anim" data-delay="110">
                                            <div className="rpp-stat-icon rpp-stat-icon--amber"><ScrollText size={18} /></div>
                                            <span className="rpp-stat-number">{totals.apostilas}</span>
                                            <span className="rpp-stat-label">Apostilas</span>
                                        </div>
                                    )}
                                    {totals.subdecks > 0 && (
                                        <div className="rpp-stat-card rpp-anim" data-delay="220">
                                            <div className="rpp-stat-icon rpp-stat-icon--blue"><Layers size={18} /></div>
                                            <span className="rpp-stat-number">{totals.subdecks}</span>
                                            <span className="rpp-stat-label">Decks</span>
                                        </div>
                                    )}
                                    {totals.flashcards > 0 && (
                                        <div className="rpp-stat-card rpp-anim" data-delay="330">
                                            <div className="rpp-stat-icon rpp-stat-icon--purple"><Zap size={18} /></div>
                                            <span className="rpp-stat-number">{totals.flashcards.toLocaleString('pt-BR')}+</span>
                                            <span className="rpp-stat-label">Flashcards</span>
                                        </div>
                                    )}
                                </div>
                            )
                        )}
                    </div>
                </section>
            )}

            {/* ── BLOCO DIFERENCIAL ── */}
            {publicPage.differentialEnabled && (
                <section className="rpp-differential-section rpp-anim" data-delay="0">
                    <div className="rpp-differential-inner">
                        <div className={`rpp-differential-layout ${publicPage.differentialRightMode === 'none' ? 'rpp-differential-solo' : ''}`}>
                            <div className="rpp-differential-left">
                                {publicPage.differentialTitle && (
                                    <h2 className="rpp-differential-title">{publicPage.differentialTitle}</h2>
                                )}
                                {publicPage.differentialContent && (
                                    <div className="rpp-differential-content">
                                        {renderDescBlocks(publicPage.differentialContent)}
                                    </div>
                                )}
                            </div>
                            {publicPage.differentialRightMode === 'animation_osce' && (
                                <div className="rpp-differential-right"><OsceAnimation scenario={publicPage.osceScenario} /></div>
                            )}
                            {publicPage.differentialRightMode === 'stat_cards' && (
                                <div className="rpp-differential-right">
                                    <div className="rpp-diff-stat-grid">
                                        {publicPage.differentialStatCards.map((card, i) => {
                                            const CardIcon = ICON_MAP[card.icon] ?? BookOpen;
                                            return (
                                                <div key={i} className="rpp-diff-stat-card rpp-anim rpp-anim--subtle" data-delay={`${i * 110}`}>
                                                    <div className="rpp-diff-stat-icon"><CardIcon size={18} /></div>
                                                    <span className="rpp-diff-stat-value">{card.value}</span>
                                                    <span className="rpp-diff-stat-label">{card.label}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            )}

            {/* ── BLOCOS DE DEMONSTRAÇÃO ── */}
            {(publicPage.contentBlocks ?? []).map(block => {
                if (
                    block.type !== 'apostila_demo' && block.type !== 'flashcards_demo' &&
                    block.type !== 'metodo_demo' && block.type !== 'estudo_ativo_demo' &&
                    block.type !== 'tutoria_demo' && block.type !== 'video_demo'
                ) return null;
                const normalizedData = block.type === 'apostila_demo'
                    ? { ...defaultApostilaDemoData(), ...(block.data as Partial<ApostilaDemoData>) }
                    : block.type === 'flashcards_demo'
                    ? { ...defaultFlashcardsDemoData(), ...(block.data as Partial<FlashcardsDemoData>) }
                    : block.type === 'metodo_demo'
                    ? { ...defaultMetodoDemoData(), ...(block.data as Partial<MetodoDemoData>) }
                    : block.type === 'estudo_ativo_demo'
                    ? { ...defaultEstudoAtivoDemoData(), ...(block.data as Partial<EstudoAtivoDemoData>) }
                    : block.type === 'tutoria_demo'
                    ? { ...defaultTutoriaDemoData(), ...(block.data as Partial<TutoriaDemoData>) }
                    : { ...defaultVideoDemoData(), ...(block.data as Partial<VideoDemoData>) };
                const variant = block.backgroundVariant === 'green' ? 'green'
                    : block.backgroundVariant === 'dark' ? 'dark'
                    : block.backgroundVariant === 'light' ? 'light'
                    : 'white';
                const sectionClass = `rpp-demoblock-section rpp-anim rpp-demoblock-section--${variant}`;
                return (
                    <DemoBlockErrorBoundary key={block.id}>
                        <section className={sectionClass}>
                            <div className="rpp-demoblock-inner">
                                {block.type === 'apostila_demo' && (
                                    <ApostilaDemoBlock data={normalizedData as ApostilaDemoData} />
                                )}
                                {block.type === 'flashcards_demo' && (
                                    <FlashcardsDemoBlock data={normalizedData as FlashcardsDemoData} />
                                )}
                                {block.type === 'metodo_demo' && (
                                    <MetodoDemoBlock data={normalizedData as MetodoDemoData} />
                                )}
                                {block.type === 'estudo_ativo_demo' && (
                                    <EstudoAtivoDemoBlock data={normalizedData as EstudoAtivoDemoData} />
                                )}
                                {block.type === 'tutoria_demo' && (
                                    <TutoriaDemoBlock data={normalizedData as TutoriaDemoData} />
                                )}
                                {block.type === 'video_demo' && (
                                    <VideoDemoBlock
                                        data={normalizedData as VideoDemoData}
                                        onPlay={() => {
                                            if (slug) makeRyanTracker(product._id, slug, 'product').track('click_video_play', { blockId: block.id });
                                        }}
                                        onProgress={(percent) => {
                                            if (slug) makeRyanTracker(product._id, slug, 'product').track('video_watch_progress', { blockId: block.id, percent });
                                        }}
                                    />
                                )}
                            </div>
                        </section>
                    </DemoBlockErrorBoundary>
                );
            })}

            {/* ── CONTEÚDO ── */}
            {spaces.length > 0 && (
                <section className="rpp-content-section" id="rpp-content-section">
                    <div className="rpp-content-inner">
                        <div className="rpp-section-header rpp-anim">
                            <h2>Conteúdo incluído</h2>
                            <p>Veja exatamente o que você está adquirindo</p>
                        </div>

                        {customContainers ? (
                            <div className="rpp-content-flat">
                                {customContainers.map(renderContentContainer)}
                            </div>
                        ) : isSingleAuto ? (
                            <div className="rpp-content-flat">
                                {renderSpaceContent(spaces[0])}
                            </div>
                        ) : (
                            <div className="rpp-spaces-list">
                                {spaces.map((space, si) => {
                                    const isExpanded = expandedSpaces.has(space._id);
                                    const hasApostilas = space.apostilas.length > 0;
                                    const hasSubdecks = space.subdecks.length > 0;

                                    return (
                                        <div key={space._id} className={`rpp-space-card ${isExpanded ? 'expanded' : ''} rpp-anim rpp-anim--card`}
                                            data-delay={Math.min(si * 100, 300)}
                                        >
                                            <div className="rpp-space-header" onClick={() => toggleSpace(space._id)}>
                                                <div className="rpp-space-index">{si + 1}</div>
                                                <div className="rpp-space-header-text">
                                                    <p className="rpp-space-name">{space.name}</p>
                                                    {(publicPage.showContentGroupCounts ?? true) && (
                                                        <div className="rpp-space-chips">
                                                            {hasApostilas && (
                                                                <span className="rpp-chip rpp-chip--amber">
                                                                    <ScrollText size={10} />{space.apostilas.length} apostil{space.apostilas.length > 1 ? 'as' : 'a'}
                                                                </span>
                                                            )}
                                                            {hasSubdecks && (
                                                                <span className="rpp-chip rpp-chip--blue">
                                                                    <Brain size={10} />{space.subdecks.length} decks
                                                                </span>
                                                            )}
                                                            {space.totalCards > 0 && (
                                                                <span className="rpp-chip rpp-chip--purple">
                                                                    <Zap size={10} />{space.totalCards.toLocaleString('pt-BR')} cards
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                    {space.description && renderDescBlocks(space.description)}
                                                </div>
                                                <ChevronDown size={17} className="rpp-space-chevron" />
                                            </div>

                                            <div className="rpp-space-body">
                                                <div className="rpp-space-body-inner">
                                                    {renderSpaceContent(space)}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* ── FECHAMENTO: "Como funciona" + "Adquira agora" unificados ── */}
            <section className="rpp-close-section" id="rpp-cta" ref={closeSectionRef}>
                <div className="rpp-close-inner">
                    <div className="rpp-close-header rpp-anim">
                        <h2 className="rpp-close-title" style={{ fontFamily: getRyanHeroFontFamily(publicPage.heroTitleFont) }}>
                            Pronto para começar?
                        </h2>
                        <div className="rpp-close-divider">
                            <span className="rpp-hero-divider-line" />
                            <span className="rpp-hero-divider-dot" />
                            <span className="rpp-hero-divider-line" />
                        </div>
                        <p className="rpp-close-desc">Três passos entre você e o acesso a {product.title}.</p>
                    </div>

                    <div className="rpp-close-steps">
                        <div className="rpp-close-step rpp-anim" data-delay="0">
                            <div className={`rpp-close-step-icon${closeGlowStep1 ? ' rpp-close-step-icon--glow' : ''}`}>
                                <LogIn size={20} />
                            </div>
                            <span className="rpp-close-step-num">01</span>
                            <div className="rpp-close-step-body">
                                <h3>Entre ou crie sua conta</h3>
                                <p>Usaremos seu e-mail para identificar uma conta existente ou criar uma nova durante o checkout.</p>
                            </div>
                        </div>
                        <div className="rpp-close-step-connector">
                            {closeTravel12 && <span className="rpp-close-orb rpp-close-orb--travel-x" />}
                        </div>
                        <div className="rpp-close-step rpp-anim" data-delay="120">
                            <div className={`rpp-close-step-icon${closeGlowStep2 ? ' rpp-close-step-icon--glow' : ''}`}>
                                <CreditCard size={20} />
                            </div>
                            <span className="rpp-close-step-num">02</span>
                            <div className="rpp-close-step-body">
                                <h3>Escolha Pix ou cartão</h3>
                                <p>O pagamento é processado automaticamente pelo Mercado Pago, com confirmação segura.</p>
                            </div>
                        </div>
                        <div className="rpp-close-step-connector">
                            {closeTravel23 && <span className="rpp-close-orb rpp-close-orb--travel-x" />}
                        </div>
                        <div className="rpp-close-step rpp-anim" data-delay="240">
                            <div className={`rpp-close-step-icon${closeGlowStep3 ? ' rpp-close-step-icon--glow' : ''}`}>
                                <CheckCircle size={20} />
                            </div>
                            <span className="rpp-close-step-num">03</span>
                            <div className="rpp-close-step-body">
                                <h3>Comece a estudar</h3>
                                <p>Assim que o pagamento for confirmado, seu acesso é liberado automaticamente na plataforma.</p>
                            </div>
                        </div>
                    </div>

                    {/* Sem a faixa de "trust" (Pagamento seguro/Acesso liberado) nem a
                        linha divisória que existiam aqui antes — botão vem direto
                        depois dos passos, preço logo abaixo dele. "Pagamento seguro
                        via Mercado Pago" mudou de lugar: agora é um item da lista de
                        confiança no checkout (RyanCheckoutPage.tsx), junto com os
                        outros avisos que já ficavam lá. */}
                    <div className="rpp-close-final rpp-anim">
                        {closeTravelDrop && (
                            <span className="rpp-close-orb-drop-track">
                                <span className="rpp-close-orb rpp-close-orb--travel-y" />
                            </span>
                        )}
                        <button
                            type="button"
                            className={`rpp-close-button${closeButtonPulse ? ' rpp-close-button--pulse' : ''}`}
                            onClick={() => {
                                if (slug) makeRyanTracker(product._id, slug, 'product').track('click_buy', { source: 'close' });
                                navigate(`/ryan/checkout/${slug}`);
                            }}
                        >
                            <CheckCircle size={19} />
                            Quero adquirir agora
                        </button>
                        {publicPage.showPriceClose && product.priceCents > 0 && (
                            <div className="rpp-close-price-wrap">
                                <span className="rpp-close-price-val">{formatPrice(product.priceCents)}</span>
                                {product.validadeTexto && (
                                    <span className="rpp-close-price-dur">{product.validadeTexto}</span>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="rpp-close-faq rpp-anim">
                        {CLOSE_FAQ.map((item, i) => {
                            const isOpen = openFaqIndex === i;
                            return (
                                <div key={item.q} className="rpp-close-faq-item">
                                    <button
                                        type="button" className="rpp-close-faq-q"
                                        onClick={() => setOpenFaqIndex(isOpen ? null : i)}
                                        aria-expanded={isOpen}
                                    >
                                        <span>{item.q}</span>
                                        {isOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                                    </button>
                                    {isOpen && <p className="rpp-close-faq-a">{item.a}</p>}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ── FOOTER ──
                O <footer> em si NÃO leva .rpp-anim: seu fundo escuro fica sempre
                sólido, só o conteúdo interno fica invisível/desliza na entrada.
                Antes, com opacity:0 no <footer> inteiro, a área ficava transparente
                até o IntersectionObserver disparar — mostrando por um instante o
                branco da página por trás em vez do verde-escuro do rodapé. */}
            <footer className="rpp-footer">
                <div className="rpp-footer-inner rpp-anim">
                    <img src="/Ryan/graia-logo-branca.png" alt="GRAIA" className="rpp-footer-logo" />
                    <p className="rpp-footer-brand">Graia Medicina</p>
                    {publicPage.whatsappNumber && (
                        <a href={whatsappDoubtUrl} target="_blank" rel="noopener noreferrer" className="rpp-footer-contact">
                            <WhatsAppIcon size={14} /> Dúvidas? Fale conosco
                        </a>
                    )}
                    <p className="rpp-footer-privacy">
                        Usamos dados de navegação anônimos para melhorar esta página.{' '}
                        <a href="/ryan/termos#privacidade" target="_blank" rel="noopener noreferrer">Saiba mais</a>
                    </p>
                </div>
            </footer>
        </div>
    );
};

export default RyanProductPage;
